import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataTableBody } from "./data-table-body";
import { DataTableBulkBar } from "./data-table-bulk-bar";
import { SortIcon } from "./data-table-helpers";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";
import type { DataTableProps } from "./types";

export function DataTable<T extends Record<string, unknown>>({
	data,
	columns,
	loading,
	isError,
	onRetry,
	page,
	pageSize,
	onPageChange,
	onPageSizeChange,
	search,
	onSearchChange,
	searchPlaceholder,
	ordering,
	onOrderingChange,
	filters,
	filterValues,
	onFilterChange,
	headerActions,
	rowKeyField = "id",
	selectedKeys,
	onSelectKey,
	onSelectAll,
	isAllSelected,
	isIndeterminate,
	bulkActions,
	onRowClick,
	pageSizeOptions,
	emptyMessage,
}: DataTableProps<T>) {
	const { t } = useTranslation();
	const selectable = selectedKeys !== undefined;
	const colCount = columns.length + (selectable ? 1 : 0);

	function handleSort(sortField: string) {
		if (!onOrderingChange) return;
		if (ordering === sortField) {
			onOrderingChange(`-${sortField}`);
		} else if (ordering === `-${sortField}`) {
			onOrderingChange("");
		} else {
			onOrderingChange(sortField);
		}
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-3">
			<DataTableToolbar
				search={search}
				onSearchChange={onSearchChange}
				searchPlaceholder={searchPlaceholder}
				filters={filters}
				filterValues={filterValues}
				onFilterChange={onFilterChange}
				headerActions={headerActions}
			/>

			<div className="relative min-h-0 flex-1">
				{selectable && bulkActions !== undefined && (
					<DataTableBulkBar
						selectedCount={selectedKeys.size}
						bulkActions={bulkActions}
					/>
				)}
				<div className="h-full min-h-0 overflow-auto rounded-md border [&_[data-slot=table-container]]:overflow-x-visible">
					<Table>
						<TableHeader className="sticky top-0 z-10">
							<TableRow>
								{selectable && (
									<TableHead className="w-10">
										<Checkbox
											checked={
												isAllSelected
													? true
													: isIndeterminate
														? "indeterminate"
														: false
											}
											onCheckedChange={onSelectAll}
											aria-label={t("dataTable.selectAll")}
										/>
									</TableHead>
								)}
								{columns.map((col) => (
									<TableHead
										key={String(col.key)}
										onClick={
											col.sortField
												? () => handleSort(col.sortField!)
												: undefined
										}
										className={
											[
												col.sortField ? "cursor-pointer select-none" : "",
												col.key === "_actions" ? "w-14 text-right" : "",
												col.headerClassName ?? "",
											]
												.filter(Boolean)
												.join(" ") || undefined
										}
									>
										<span className="flex items-center gap-1.5">
											{col.label}
											{col.sortField && (
												<SortIcon field={col.sortField} ordering={ordering} />
											)}
										</span>
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<DataTableBody
							data={data}
							columns={columns}
							loading={loading}
							isError={isError}
							onRetry={onRetry}
							selectable={selectable}
							colCount={colCount}
							rowKeyField={rowKeyField}
							selectedKeys={selectedKeys}
							onSelectKey={onSelectKey}
							onRowClick={onRowClick}
							emptyMessage={emptyMessage}
						/>
					</Table>
				</div>
			</div>

			{data && (
				<DataTablePagination
					page={page}
					pageSize={pageSize}
					count={data.count}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					pageSizeOptions={pageSizeOptions}
					hasPrevious={!!data.previous}
					hasNext={!!data.next}
				/>
			)}
		</div>
	);
}
